/**
 * The hollow. A figure that is not there.
 *
 * The director never spends threat budget on these — they are produced by low
 * sanity and by fakeouts, and they cannot touch the player. A hollow stands at
 * the end of a corridor and waits to be noticed. Look straight at it twice and
 * it is gone, which is exactly what the player will do, and exactly what will
 * make them doubt the next one.
 */

import { clamp01 } from '../../core/math';
import { holdStill, moveTo, randomWalkableNear, setMode, type Behavior } from '../brain';
import { ENTITY_TUNING } from '../defs';
import { playerStaringAt } from '../perception';

const T = ENTITY_TUNING.hollow;

export const hollowBehavior: Behavior = {
  kind: 'hollow',

  spawn(a, w) {
    a.turnRate = 1.1;
    a.counter = 0;
    a.lookLatch = false;
    a.timer = a.rng.range(T.lifetime[0], T.lifetime[1]);
    a.dest.x = a.state.pos.x;
    a.dest.z = a.state.pos.z;
    setMode(a, w, 'dormant');
  },

  think(a, w) {
    const s = a.state;
    a.timer -= w.dt;
    if (a.timer <= 0) {
      a.retire = true;
      return;
    }

    // "Directly at it", not "somewhere on screen". A hollow caught in the
    // corner of the eye must survive, or none of them are ever unsettling.
    const staring = s.visible && playerStaringAt(w.grid, w.player, s.pos, T.lookCos);
    if (staring && !a.lookLatch) {
      a.lookLatch = true;
      a.counter++;
      if (a.counter >= T.looksToVanish) {
        w.audio.play('whisper', { pos: s.pos, gain: 0.5 });
        a.retire = true;
        return;
      }
    } else if (!staring) {
      a.lookLatch = false;
    }

    s.awareness = s.visible ? clamp01(s.awareness + w.dt * 1.2) : Math.max(0, s.awareness - w.dt * 0.8);
    if (s.visible) w.dread(s, a.def.sanityDrain);

    // It faces the player and does not approach. Between looks it drifts a few
    // metres, so the second sighting is never quite where the first one was.
    a.desiredYaw = Math.atan2(w.player.pos.x - s.pos.x, -(w.player.pos.z - s.pos.z));

    if (s.visible) {
      holdStill(a);
      return;
    }

    a.timer2 -= w.dt;
    if (a.timer2 <= 0) {
      a.timer2 = a.rng.range(3, 8);
      const spot = randomWalkableNear(w.grid, a.rng, s.pos, 2, 7);
      if (spot) {
        a.dest.x = spot.x;
        a.dest.z = spot.z;
      }
    }
    moveTo(a, w, a.dest, a.def.walkSpeed);
  },
};
